import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { MessageSquare, RefreshCw, Mail, CheckCircle2, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

const STATUS_CONFIG = {
  new: { label: 'New', bg: '#FEE2E2', color: '#DC2626' },
  in_progress: { label: 'In Progress', bg: '#FFF8EC', color: '#D9A245' },
  resolved: { label: 'Resolved', bg: '#DDF5F1', color: '#008C86' },
  archived: { label: 'Archived', bg: '#F3F4F6', color: '#6B7280' },
};

export default function AdminContactPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API}/admin/contacts${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/admin/contacts/${id}/status`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    toast.success('Status updated');
    load();
  };

  return (
    <AdminLayout title="Inquiries" breadcrumb="Contact / Inquiries">
      <div style={{ maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 20, fontWeight: 800, color: '#07121f', letterSpacing: '-.05em', margin: '0 0 4px' }}>
              Contact Inquiries
            </h1>
            <p style={{ fontSize: 13, color: 'var(--op-muted)', margin: 0 }}>{items.length} entr{items.length !== 1 ? 'ies' : 'y'}</p>
          </div>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--op-muted)', cursor: 'pointer' }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['all', 'new', 'in_progress', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: filter === s ? '#081D30' : 'rgba(221,212,197,0.4)',
                color: filter === s ? '#fff' : 'var(--op-muted)',
              }}
            >
              {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="contacts-list">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--op-muted)' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--op-muted)', background: '#fff', borderRadius: 12, border: '1px solid rgba(221,212,197,0.7)' }}>
              <MessageSquare size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>No inquiries found</p>
            </div>
          ) : items.map((item, i) => {
            const s = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
            return (
              <div key={item.id || i} data-testid="contact-item" style={{ background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#07121f' }}>{item.first_name} {item.last_name}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.label}</span>
                      {item.inquiry_type && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(8,29,48,.08)', color: '#081D30' }}>{item.inquiry_type}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: 'var(--op-muted)', marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} />{item.email}</span>
                      {item.organization && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={11} />{item.organization}</span>}
                      {item.created_at && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} />{new Date(item.created_at).toLocaleDateString()}</span>}
                    </div>
                    {item.subject && <p style={{ fontSize: 12, fontWeight: 600, color: '#07121f', margin: '0 0 4px' }}>{item.subject}</p>}
                    {item.message && <p style={{ fontSize: 12, color: 'var(--op-muted)', margin: 0, lineHeight: 1.5 }}>{item.message.slice(0, 200)}{item.message.length > 200 ? '…' : ''}</p>}
                  </div>
                  <select
                    value={item.status || 'new'}
                    onChange={e => updateStatus(item.id, e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 7, fontSize: 12, background: '#fff', color: '#07121f', cursor: 'pointer', flexShrink: 0 }}
                  >
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
